import {
    FormProvider,
    RHFRadioGroup,
    RHFSelect,
    RHFTextField,
} from "@callveriq/components/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, styled, Typography, useTheme } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import {
    MomentBlockFormType,
    MomentLocationTypes,
    SingleResponseNode,
} from "../../../types/bert";
import useNodeContext from "../../hooks/useNodeContext";
import {
    BertNodeKeys,
    momentLocationOptions,
    OptionTypeId,
    participantOption,
} from "../../constants";
import { useReactFlowStateContextProvider } from "../../hooks/useReactFlowStateContextProvider";
import BlockHeader from "./BlockHeader";
import {
    generateCleanNanoID,
    generateStartEndTime,
    isDefined,
} from "@callveriq/utils/helper/common.helper";
import RHFPhraseListField from "@callveriq/modules/settings/modules/auditManager/components/bertRuleEngine/components/RHFPhraseListField";

import { useEffect, useState } from "react";
import { pxToRem } from "@callveriq/utils/getFontValue";
import { showToast } from "@callveriq/utils/toast";
import { CreateUpdateToastSettings } from "@callveriq/config/toast.config";
import { AntSwitch } from "@callveriq/components/custom_components/Switch/AntSwitch";
import { useNodeInfoContext } from "../../context/NodeInfoContext";
import GenerateAiDescription from "@callveriq/modules/settings/components/reusables/layouts/GenerateAiDescription";
import AttachLmsDocuments from "@callveriq/modules/settings/components/reusables/components/AttachLmsDocuments";
import GenericSingleSelect from "@callveriq/components/select/GenericSingleSelect";
import { useGetExistingBlocks } from "../../hooks/useGetExistingBlocks";
import InfiniteScrollSelect from "@callveriq/components/select/InfiniteScrollSelect";
import useDebounce from "@callveriq/hooks/useDebounce";

const schema = yup.object().shape({
    name: yup.string().required("Name is required"),
    speaker: yup.string().required("Speaker is required"),
    phrases: yup.array(),
    window: yup
        .number()
        .required("Window is required")
        .typeError("Window size must be a number")
        .required("Please provide plan cost.")
        .min(1, "Window size Must be between 1 to 3")
        .max(3, "Window size Must be between 1 to 3"),
    location: yup.string().required("Location required"),
    from: yup.string().when("location", {
        is: (location: MomentBlockFormType["location"]) =>
            location === "specific",
        then: (s) => s.required("Required"),
        otherwise: (s) => s,
    }),
    moment: yup.object<MomentBlockFormType<string[]>>().when("from", {
        is: (from: MomentBlockFormType["from"]) => from === "moment",
        then: (s) => s.required("Required"),
        otherwise: (s) => s.nullable(),
    }),
    seconds: yup
        .number()
        .min(0, "Min : 0")
        .max(9999, "Max : 9999")
        .when("location", {
            is: (location: MomentBlockFormType["location"]) =>
                location === "specific",
            then: (s) => s.required("Seconds required"),
            otherwise: (s) => s,
        }),
    operation: yup.string().when("from", {
        is: (from: MomentBlockFormType["from"]) => from === "moment",
        then: (s) => s.required("Required"),
        otherwise: (s) => s,
    }),
    prompt: yup
        .string()
        .nullable()
        .when("is_prompt", {
            is: (is_prompt: boolean) => is_prompt,
            then: (s) =>
                s
                    .required("Prompt is required")
                    .typeError("Prompt is required"),
            otherwise: (s) => s,
        }),
    lms_reference_doc_ids: yup.array(yup.number()),
    lms_reference_category_ids: yup.array(yup.number()),
    id: yup.string().nullable(),
    lms_expression: yup.string(),
});

const StyledGenericSingleSelect = styled(GenericSingleSelect)(({ theme }) => ({
    "&.MuiAutocomplete-root": {
        width: "100%",
        height: "18px",
        "& *": {
            outline: "none !important",
            border: "none !important",
        },
        "& .MuiFormControl-root": {
            height: "22px",
        },
        "& .MuiInputBase-root": {
            padding: "0px !important",
            "& .MuiInputBase-input": {
                padding: "0px !important",
                fontSize: pxToRem(12),
                color: `${theme.palette.primary.main} !important`,
                fontWeight: "500",
                "::placeholder": {
                    color: `${theme.palette.primary.main} !important`,
                    opacity: "1 !important",
                    fontWeight: "500",
                },
            },
        },
        "& .MuiButtonBase-root": {
            display: "none",
        },
    },
}));

const formatMomentData: (
    e: MomentBlockFormType<string[]>
) => MomentBlockFormType = (e) => {
    const { phrase, ...rest } = e;
    return {
        ...rest,
        phrase: phrase?.map((phrase) => ({ id: phrase, phrase })) ?? [],
    };
};

export default function MomentsBlock({
    showCreateButton = true,
}: {
    showCreateButton?: boolean;
}) {
    const {
        sourceId,
        direction,
        blockParentId,
        nodeDataToUpdate,
        nodeIdToUpdate,
        onNodeSaveCallBack,
    } = useNodeContext();
    const { nodeType } = useNodeInfoContext();
    const { createSingleResponseNode, updateSingleResponseNode } =
        useReactFlowStateContextProvider();
    const theme = useTheme();
    const [query, setQuery] = useState("");

    const debouncedQuery = useDebounce(query, 1000);
    const {
        data: momentNodes,
        hasNext,
        fetchNext,
        isLoading,
        isFetching,
    } = useGetExistingBlocks({
        block_name: debouncedQuery,
        type: BertNodeKeys.singleBlockNode,
        data_type: "moment",
    });
    const defaultValues: MomentBlockFormType = {
        name: "",
        location: "anywhere",
        speaker: "anyone",
        window: 1,
        phrase: [],
        prompt: null,
        is_prompt: nodeType === "condition_gpt" ? true : false,
        lms_reference_doc_ids: [],
        lms_reference_category_ids: [],
        id: null,
        start_time: null,
        end_time: null,
        lms_expression: "",
    };
    const momentToUpdate = isDefined(nodeDataToUpdate)
        ? //if the nodeDataToUpdate is a single key object, is is the has only name key then we need to add the default values to the object
          Object.keys(nodeDataToUpdate).length === 1
            ? { ...defaultValues, ...(nodeDataToUpdate as MomentBlockFormType) }
            : formatMomentData(
                  nodeDataToUpdate as MomentBlockFormType<string[]>
              )
        : null;

    const methods = useForm<MomentBlockFormType>({
        resolver: yupResolver(schema),
        values: isDefined(momentToUpdate)
            ? { ...momentToUpdate }
            : defaultValues,
    });

    const { handleSubmit, watch, setValue, control, setError } = methods;
    const location = watch("location");
    const from = `${watch("from")}.${watch("operation")}`;
    const is_prompt = watch("is_prompt");

    const onSubmit = async (values: MomentBlockFormType) => {
        if (!is_prompt && values.phrase.length === 0) {
            showToast({
                ...CreateUpdateToastSettings,
                type: "error",
                message: "At least one phrase is required",
            });
            return;
        }
        const { start_time, end_time } = generateStartEndTime(values);

        if (isDefined(nodeIdToUpdate)) {
            updateSingleResponseNode<MomentBlockFormType<string[]>>({
                data: {
                    ...values,
                    phrase: values.phrase.map((phrase) => phrase.phrase),
                    start_time,
                    end_time,
                },
                sourceId: nodeIdToUpdate,
                blockParentId,
            });
        } else {
            const id = generateCleanNanoID(10);
            createSingleResponseNode<SingleResponseNode<"moment">>({
                data: {
                    type: "moment",
                    is_not: false,
                    metadata: {
                        ...values,
                        phrase: values.phrase.map((phrase) => phrase.phrase),
                        start_time,
                        end_time,
                        id,
                    },
                },
                sourceId,
                direction,
                blockParentId,
            });
        }
        onNodeSaveCallBack();
    };

    useEffect(() => {
        if (location === "anywhere") {
            setValue("seconds", undefined);
            setValue("from", undefined);
        }
    }, [location]);

    const name = watch("name");
    const description = watch("prompt");

    return (
        <FormProvider
            methods={methods}
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col h-full"
        >
            <BlockHeader title="Moments" showCreateButton={showCreateButton} />
            <Box
                sx={{
                    width: "100%",
                }}
                className="flex-1"
            >
                <Stack gap={1.5} className="relative h-full">
                    <RHFTextField
                        name="name"
                        className="w-full"
                        variant="outlined"
                        size="small"
                        placeholder="Moment Name"
                        label="Moment Name"
                    />
                    <Box className="flex flex-row-reverse items-center gap-2">
                        <AntSwitch
                            size="small"
                            checked={is_prompt}
                            onChange={(e) => {
                                setValue("is_prompt", e.target.checked);
                                setValue("lms_reference_doc_ids", []);
                                setValue("lms_reference_category_ids", []);
                            }}
                            sx={{
                                transform: "scale(0.7)",
                            }}
                            disabled={nodeType === "condition_gpt"}
                        />
                        <Typography variant="small">Prompt Based</Typography>
                    </Box>
                    {is_prompt ? (
                        <>
                            <RHFTextField
                                name="lms_expression"
                                label="Expression"
                            />
                            <RHFTextField
                                name="prompt"
                                label="Prompt"
                                multiline
                                rows={4}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <GenerateAiDescription
                                                payload={{
                                                    feature_name:
                                                        "moment_block",
                                                    feature_parameter_name:
                                                        name,
                                                    feature_description_char_limit: 500,
                                                    feature_parameter_description:
                                                        description ?? "",
                                                }}
                                                onApply={(value) => {
                                                    setValue("prompt", value);
                                                }}
                                            />
                                        ),
                                    },
                                }}
                            />
                            <AttachLmsDocuments
                                handleSave={(value) => {
                                    setValue(
                                        "lms_reference_doc_ids",
                                        value.files
                                    );
                                    setValue(
                                        "lms_reference_category_ids",
                                        value.folders
                                    );
                                }}
                                initialValue={{
                                    folders:
                                        watch("lms_reference_category_ids") ??
                                        [],
                                    files: watch("lms_reference_doc_ids") ?? [],
                                }}
                            />
                        </>
                    ) : (
                        <>
                            <RHFPhraseListField />
                            <Box className="flex gap-2">
                                <RHFSelect
                                    name="speaker"
                                    className="w-full nodrag nopan"
                                    size="small"
                                    options={participantOption}
                                    label="Spoken By"
                                    variant="outlined"
                                />
                                <RHFTextField
                                    name="window"
                                    className="w-full"
                                    variant="outlined"
                                    size="small"
                                    placeholder="Window"
                                    label="Window"
                                />
                            </Box>

                            <Typography
                                variant="medium"
                                sx={{ fontWeight: 600 }}
                            >
                                Location
                            </Typography>
                            <RHFRadioGroup<"specific" | "anywhere">
                                name="location"
                                options={[
                                    { id: "anywhere", label: "Anywhere" },
                                    {
                                        id: "specific",
                                        label: "Specific Location",
                                    },
                                ]}
                                value={location}
                                handleChange={(value) => {
                                    setValue("location", value);
                                }}
                                sx={{
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: "0.876rem",
                                    },
                                    ml: -1,
                                }}
                                className="flex"
                            />
                            {location === "specific" ? (
                                <Box
                                    className="flex items-center"
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        gap: 2,
                                        background: "rgba(153, 153, 153, 0.1)",
                                        borderRadius: "6px",
                                    }}
                                >
                                    <Typography
                                        variant="small"
                                        className="font-semibold"
                                    >
                                        Detect this moment
                                    </Typography>
                                    <Box className="!w-[100px] relative">
                                        <RHFTextField
                                            name="seconds"
                                            variant="filled"
                                            placeholder=""
                                            slotProps={{
                                                input: {
                                                    endAdornment: (
                                                        <Typography variant="small">
                                                            Sec
                                                        </Typography>
                                                    ),
                                                },
                                            }}
                                            sx={{
                                                "& input[type=number]": {
                                                    MozAppearance: "textfield",
                                                },
                                                "& input[type=number]::-webkit-outer-spin-button":
                                                    {
                                                        WebkitAppearance:
                                                            "none",
                                                        margin: 0,
                                                    },
                                                "& input[type=number]::-webkit-inner-spin-button":
                                                    {
                                                        WebkitAppearance:
                                                            "none",
                                                        margin: 0,
                                                    },
                                                "& .MuiInputBase-root > fieldset":
                                                    {
                                                        border: "none",
                                                    },
                                                "& .MuiFilledInput-input": {
                                                    py: 1,
                                                    px: 1.5,
                                                    fontSize: "0.75rem",
                                                },
                                            }}
                                            required={true}
                                            type="number"
                                        />
                                    </Box>
                                    <Box
                                        className={`${
                                            from.includes("moment_id")
                                                ? "w-[85px]"
                                                : "flex-1"
                                        }`}
                                    >
                                        <Controller
                                            name="from"
                                            control={control}
                                            render={({
                                                fieldState: { error },
                                            }) => {
                                                return (
                                                    <StyledGenericSingleSelect
                                                        value={
                                                            from as OptionTypeId
                                                        }
                                                        autocompleteProps={{
                                                            options:
                                                                momentLocationOptions,
                                                        }}
                                                        className="nodrag nopan"
                                                        label=""
                                                        setValue={(value) => {
                                                            const vStr = `${value}`;
                                                            const [
                                                                from,
                                                                operation,
                                                            ] = vStr.split(".");
                                                            setValue(
                                                                "operation",
                                                                operation as
                                                                    | "+"
                                                                    | "-"
                                                                    | undefined
                                                            );
                                                            setValue(
                                                                "from",
                                                                from as MomentLocationTypes
                                                            );
                                                            setError("from", {
                                                                message: "",
                                                            });
                                                        }}
                                                        helperText={
                                                            error?.message
                                                        }
                                                        error={!!error?.message}
                                                    />
                                                );
                                            }}
                                        />
                                    </Box>

                                    {from.includes("moment") ? (
                                        <Box className="flex-1">
                                            <Controller<
                                                MomentBlockFormType<string[]>
                                            >
                                                name="moment"
                                                render={({
                                                    field: _,
                                                    fieldState: { error },
                                                }) => {
                                                    return (
                                                        <InfiniteScrollSelect
                                                            label=""
                                                            options={[
                                                                ...(watch(
                                                                    "moment"
                                                                )?.id
                                                                    ? [
                                                                          {
                                                                              id:
                                                                                  watch(
                                                                                      "moment"
                                                                                  )
                                                                                      ?.id ||
                                                                                  "",
                                                                              label:
                                                                                  watch(
                                                                                      "moment"
                                                                                  )
                                                                                      ?.name ||
                                                                                  "",
                                                                          },
                                                                      ]
                                                                    : []),
                                                                ...(momentNodes?.nodes
                                                                    ?.filter(
                                                                        (e) =>
                                                                            !isDefined(
                                                                                (
                                                                                    e
                                                                                        .data
                                                                                        .metadata as MomentBlockFormType<
                                                                                        string[]
                                                                                    >
                                                                                )
                                                                                    .moment
                                                                            )
                                                                    )
                                                                    .map(
                                                                        (
                                                                            e
                                                                        ) => ({
                                                                            id:
                                                                                (
                                                                                    e
                                                                                        .data
                                                                                        .metadata as MomentBlockFormType<
                                                                                        string[]
                                                                                    >
                                                                                )
                                                                                    .id ||
                                                                                "",
                                                                            label: e
                                                                                .data
                                                                                .metadata
                                                                                .name,
                                                                        })
                                                                    )
                                                                    ?.filter(
                                                                        (e) =>
                                                                            e.id !==
                                                                            watch(
                                                                                "moment"
                                                                            )
                                                                                ?.id
                                                                    ) || []),
                                                            ]}
                                                            onQueryChange={(
                                                                value,
                                                                reason
                                                            ) => {
                                                                if (
                                                                    reason !==
                                                                    "select"
                                                                )
                                                                    setQuery(
                                                                        value ??
                                                                            ""
                                                                    );
                                                            }}
                                                            value={
                                                                watch("moment")
                                                                    ?.id ?? null
                                                            }
                                                            setValue={(id) => {
                                                                const data =
                                                                    momentNodes?.nodes.find(
                                                                        (e) =>
                                                                            (
                                                                                e
                                                                                    .data
                                                                                    .metadata as MomentBlockFormType<
                                                                                    string[]
                                                                                >
                                                                            )
                                                                                .id ===
                                                                            id
                                                                    )?.data
                                                                        .metadata as
                                                                        | MomentBlockFormType<
                                                                              string[]
                                                                          >
                                                                        | undefined;
                                                                if (data) {
                                                                    setValue(
                                                                        "moment",
                                                                        data
                                                                    );
                                                                }
                                                            }}
                                                            className="flex-1"
                                                            virtualizeProps={{
                                                                fetchNext,
                                                                hasNext,
                                                                isFetching,
                                                                isLoading,
                                                            }}
                                                            size="small"
                                                            error={!!error}
                                                            helperText={
                                                                error?.message
                                                            }
                                                            sx={{
                                                                width: "100%",
                                                                height: "18px",
                                                                "& *": {
                                                                    outline:
                                                                        "none !important",
                                                                    border: "none !important",
                                                                },
                                                                "& .MuiFormControl-root":
                                                                    {
                                                                        height: "22px",
                                                                    },
                                                                "& .MuiInputBase-root":
                                                                    {
                                                                        padding:
                                                                            "0px !important",
                                                                        "& .MuiInputBase-input":
                                                                            {
                                                                                padding:
                                                                                    "0px !important",
                                                                                fontSize:
                                                                                    pxToRem(
                                                                                        12
                                                                                    ),
                                                                                color: `${theme.palette.primary.main} !important`,
                                                                                fontWeight:
                                                                                    "500",
                                                                                "::placeholder":
                                                                                    {
                                                                                        color: `${theme.palette.primary.main} !important`,
                                                                                        opacity:
                                                                                            "1 !important",
                                                                                        fontWeight:
                                                                                            "500",
                                                                                    },
                                                                            },
                                                                    },
                                                                "& .MuiButtonBase-root":
                                                                    {
                                                                        display:
                                                                            "none",
                                                                    },
                                                            }}
                                                        />
                                                    );
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <></>
                                    )}
                                </Box>
                            ) : null}
                        </>
                    )}
                </Stack>
            </Box>
        </FormProvider>
    );
}
